import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { 
  Users, FolderOpen, MessageSquare, ArrowLeft, RefreshCw, 
  Mail, Calendar, ChevronDown, ChevronUp, Loader2, FileText,
  Download, Phone, CheckCircle, Clock, ExternalLink, Edit, 
  Key, Trash2, Save, X, Shield, Crown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'sessions') {
        const response = await axios.get(`${API}/admin/sessions`);
        setSessions(response.data);
      } else if (activeTab === 'users') {
        const response = await axios.get(`${API}/admin/users`);
        setUsers(response.data);
      } else if (activeTab === 'projects') {
        const response = await axios.get(`${API}/admin/projects`);
        setProjects(response.data);
      } else if (activeTab === 'conversations') {
        const response = await axios.get(`${API}/admin/conversations`);
        setConversations(response.data);
      } else if (activeTab === 'documents') {
        const response = await axios.get(`${API}/admin/documents`);
        setDocuments(response.data);
      } else if (activeTab === 'contacts') {
        const response = await axios.get(`${API}/admin/contact-requests`);
        setContactRequests(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadDocument = async (projectId, docType, fileName) => {
    try {
      const response = await axios.get(
        `${API}/admin/documents/${projectId}/${docType}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const markContactHandled = async (requestId) => {
    try {
      await axios.patch(`${API}/admin/contact-requests/${requestId}`);
      toast.success('Marked as handled');
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSaveUser = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, editForm);
      toast.success('User updated successfully');
      setEditingUser(null);
      setEditForm({});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"? This will delete all their projects and data.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: Users, count: users.length },
    { id: 'projects', label: 'Projects', icon: FolderOpen, count: projects.length },
    { id: 'conversations', label: 'Conversations', icon: MessageSquare, count: conversations.length },
    { id: 'documents', label: 'Documents', icon: FileText, count: documents.length },
    { id: 'contacts', label: 'Contact Requests', icon: Phone, count: contactRequests.length },
    { id: 'sessions', label: 'Widget Sessions', icon: Mail, count: sessions.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <h1 className="text-xl font-semibold">Admin Console</h1>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadData}
            disabled={loading}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'bg-slate-900' : ''}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
              {!loading && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">
                    {users.length} registered users
                  </p>
                </div>
                <ScrollArea className="h-[600px]">
                  {users.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No registered users yet
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {users.map((u) => (
                        <div key={u.id} className="p-4 hover:bg-slate-50">
                          {editingUser === u.id ? (
                            // Edit Mode
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-slate-500">Name</label>
                                  <Input 
                                    value={editForm.name || ''} 
                                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Email</label>
                                  <Input 
                                    value={editForm.email || ''} 
                                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <label className="text-xs text-slate-500">Subscription Tier</label>
                                  <select 
                                    value={editForm.subscription_tier || 'free'}
                                    onChange={(e) => setEditForm({...editForm, subscription_tier: e.target.value})}
                                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                                  >
                                    <option value="free">Free</option>
                                    <option value="pro">Pro</option>
                                    <option value="enterprise">Enterprise</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Unlimited Messages</label>
                                  <select 
                                    value={editForm.unlimited_messages ? 'yes' : 'no'}
                                    onChange={(e) => setEditForm({...editForm, unlimited_messages: e.target.value === 'yes'})}
                                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                                  >
                                    <option value="no">No</option>
                                    <option value="yes">Yes</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500">Admin Access</label>
                                  <select 
                                    value={editForm.is_admin ? 'yes' : 'no'}
                                    onChange={(e) => setEditForm({...editForm, is_admin: e.target.value === 'yes'})}
                                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                                  >
                                    <option value="no">No</option>
                                    <option value="yes">Yes</option>
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2 pt-2">
                                <Button 
                                  size="sm" 
                                  onClick={() => handleSaveUser(u.id)}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {setEditingUser(null); setEditForm({});}}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : resetPasswordUser === u.id ? (
                            // Reset Password Mode
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white font-medium">
                                    {u.name?.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{u.name}</p>
                                  <p className="text-sm text-slate-500">{u.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Input 
                                  type="text"
                                  placeholder="New password (min 6 characters)"
                                  value={newPassword}
                                  onChange={(e) => setNewPassword(e.target.value)}
                                  className="flex-1"
                                />
                                <Button 
                                  size="sm"
                                  onClick={() => handleResetPassword(u.id)}
                                  disabled={newPassword.length < 6}
                                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                                >
                                  Reset
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {setResetPasswordUser(null); setNewPassword('');}}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-medium">
                                  {u.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">{u.name}</p>
                                  {u.is_admin && (
                                    <Shield className="h-4 w-4 text-purple-600" title="Admin" />
                                  )}
                                  {u.unlimited_messages && (
                                    <Crown className="h-4 w-4 text-amber-500" title="Unlimited Messages" />
                                  )}
                                </div>
                                <p className="text-sm text-slate-500 truncate">{u.email}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  u.subscription_tier === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                                  u.subscription_tier === 'pro' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {u.subscription_tier || 'free'}
                                </span>
                                <p className="text-xs text-slate-400 mt-1">{formatDate(u.created_at)}</p>
                              </div>
                              <div className="flex gap-1 ml-2">
                                <button
                                  onClick={() => {
                                    setEditingUser(u.id);
                                    setEditForm({
                                      name: u.name,
                                      email: u.email,
                                      subscription_tier: u.subscription_tier || 'free',
                                      unlimited_messages: u.unlimited_messages || false,
                                      is_admin: u.is_admin || false
                                    });
                                  }}
                                  className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                  title="Edit user"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setResetPasswordUser(u.id)}
                                  className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                  title="Reset password"
                                >
                                  <Key className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id, u.name)}
                                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete user"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">
                    {projects.length} projects across all users
                  </p>
                </div>
                <ScrollArea className="h-[600px]">
                  {projects.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No projects yet
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {projects.map((project) => (
                        <div key={project.id} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{project.name}</p>
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                {project.description || 'No description'}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <p className="text-xs text-slate-400">
                                  Owner: {project.user_name} ({project.user_email})
                                </p>
                                <span className="text-xs text-slate-400">•</span>
                                <p className="text-xs text-slate-400">
                                  {project.message_count || 0} messages
                                </p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-4">
                              <p className="text-xs text-slate-400">Last updated</p>
                              <p className="text-sm text-slate-600">{formatDate(project.updated_at)}</p>
                              {(project.business_case_file || project.project_plan_file) && (
                                <p className="text-xs text-emerald-600 mt-1">Has documents</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Conversations Tab */}
            {activeTab === 'conversations' && (
              <div>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">
                    {conversations.length} projects with conversations
                  </p>
                </div>
                <ScrollArea className="h-[600px]">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No conversations yet
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {conversations.map((conv) => (
                        <div key={conv.project_id} className="p-4">
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => setExpandedItem(
                              expandedItem === conv.project_id ? null : conv.project_id
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                <MessageSquare className="h-5 w-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{conv.project_name}</p>
                                <p className="text-sm text-slate-500">
                                  {conv.user_name} • {conv.message_count} messages
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-400">{formatDate(conv.updated_at)}</span>
                              {expandedItem === conv.project_id ? (
                                <ChevronUp className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded messages */}
                          {expandedItem === conv.project_id && (
                            <div className="mt-4 ml-13 pl-4 border-l-2 border-slate-200 space-y-3 max-h-96 overflow-y-auto">
                              {conv.messages.map((msg, idx) => (
                                <div 
                                  key={idx}
                                  className={`text-sm p-3 rounded-lg ${
                                    msg.role === 'user' 
                                      ? 'bg-slate-100 text-slate-700' 
                                      : 'bg-amber-50 text-amber-900'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-semibold text-xs uppercase tracking-wide">
                                      {msg.role === 'user' ? 'User' : 'Coach'}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      {formatDate(msg.created_at)}
                                    </span>
                                  </div>
                                  <p className="whitespace-pre-wrap">{msg.content}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">
                    {documents.length} uploaded documents
                  </p>
                </div>
                <ScrollArea className="h-[600px]">
                  {documents.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No documents uploaded yet
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {documents.map((doc) => (
                        <div key={doc.id} className="p-4 flex items-center gap-4 hover:bg-slate-50">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900">{doc.file_name}</p>
                            <p className="text-sm text-slate-500">
                              {doc.file_type} • Project: {doc.project_name}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Uploaded by {doc.user_name} ({doc.user_email})
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-slate-400">{formatDate(doc.uploaded_at)}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadDocument(
                                doc.project_id, 
                                doc.file_type === 'Document' ? 'business_case' : 'project_plan',
                                doc.file_name
                              )}
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Contact Requests Tab */}
            {activeTab === 'contacts' && (
              <div>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">
                    {contactRequests.length} contact requests
                  </p>
                </div>
                <ScrollArea className="h-[600px]">
                  {contactRequests.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No contact requests yet
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {contactRequests.map((req) => (
                        <div key={req.id} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                req.status === 'handled' ? 'bg-emerald-100' : 'bg-amber-100'
                              }`}>
                                {req.status === 'handled' ? (
                                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                                ) : (
                                  <Clock className="h-5 w-5 text-amber-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{req.name}</p>
                                <p className="text-sm text-slate-500">{req.email}</p>
                                {req.project_name && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    Project: {req.project_name}
                                  </p>
                                )}
                                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.message}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-4">
                              <span className="text-xs text-slate-400">{formatDate(req.created_at)}</span>
                              {req.status !== 'handled' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => markContactHandled(req.id)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Mark Handled
                                </Button>
                              )}
                              <a
                                href={`mailto:${req.email}`}
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Reply via email
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Widget Sessions Tab */}
            {activeTab === 'sessions' && (
              <div>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">
                    {sessions.length} widget sessions (email-only users)
                  </p>
                </div>
                <ScrollArea className="h-[600px]">
                  {sessions.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No widget sessions yet
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {sessions.map((session) => (
                        <div key={session.id} className="p-4">
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => setExpandedItem(
                              expandedItem === session.id ? null : session.id
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                <Mail className="h-5 w-5 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{session.email}</p>
                                <p className="text-sm text-slate-500">
                                  {session.message_count || 0} messages • {formatDate(session.updated_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {session.converted_to_user && (
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                  Converted
                                </span>
                              )}
                              {expandedItem === session.id ? (
                                <ChevronUp className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded messages */}
                          {expandedItem === session.id && session.messages && (
                            <div className="mt-4 ml-13 pl-4 border-l-2 border-slate-100 space-y-2">
                              {session.messages.map((msg, idx) => (
                                <div 
                                  key={idx}
                                  className={`text-sm p-2 rounded-lg ${
                                    msg.role === 'user' 
                                      ? 'bg-slate-100 text-slate-700' 
                                      : 'bg-amber-50 text-amber-800'
                                  }`}
                                >
                                  <span className="font-medium">
                                    {msg.role === 'user' ? 'User' : 'Coach'}:
                                  </span>{' '}
                                  {msg.content}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
